import { Data, Route } from '@angular/router';
import { AppPermission, ROUTE_PERMISSION_KEY } from '@core/constants/permissions.const';

/** Estructura mínima común a `Route` y `ActivatedRouteSnapshot`. */
export interface RouteWithData {
  data?: Data;
}

/**
 * Lee el permiso declarado en `data.permission` de una ruta.
 * @param route Ruta de configuración o snapshot activo.
 * @returns El permiso declarado, o `null` si la ruta no está protegida.
 */
export function getRoutePermission(route: RouteWithData | null | undefined): AppPermission | null {
  const declared: unknown = route?.data?.[ROUTE_PERMISSION_KEY];
  return typeof declared === 'string' ? (declared as AppPermission) : null;
}

/**
 * Filtra rutas dejando solo las que el usuario puede ver. Las rutas sin permiso
 * declarado se consideran públicas dentro del área autenticada.
 * @param routes Rutas a filtrar (hijas de un shell, ítems de menú o tarjetas).
 * @param can Predicado de autorización, normalmente `PermissionsService.has`.
 * @returns Las rutas visibles para el usuario actual.
 */
export function filterRoutesByPermission<T extends RouteWithData>(
  routes: readonly T[],
  can: (permission: AppPermission) => boolean,
): T[] {
  return routes.filter((route) => {
    const permission = getRoutePermission(route);
    return permission === null || can(permission);
  });
}

/**
 * Resuelve la primera ruta hija accesible, útil para redirigir cuando la ruta
 * por defecto de un módulo no está permitida para el perfil actual.
 * @param routes Rutas hijas candidatas.
 * @param can Predicado de autorización.
 * @returns La primera ruta accesible con `path` definido, o `null`.
 */
export function firstAccessibleRoute(
  routes: readonly Route[],
  can: (permission: AppPermission) => boolean,
): Route | null {
  return filterRoutesByPermission(routes, can).find((route) => !!route.path) ?? null;
}
